import { useState, useEffect } from 'react';
import { supabase } from '../supabase.config';

/**
 * Get initial study spots (first 12)
 * @returns {Promise<Array>} Array of study spots
 */
export const getInitialSpots = async () => {
  try {
    const { data, error } = await supabase
      .from('StudySpot')
      .select(`
        *,
        building:Building(*),
        photos:Photo(*)
      `)
      .limit(12);

    if (error) throw error;

    // Transform data to match expected format
    return data.map(spot => ({
      id: spot.id,
      name: spot.name,
      building: spot.building?.name || '',
      location: spot.location,
      description: spot.description,
      features: spot.features || [],
      noiseLevel: spot.noiseLevel,
      spaceType: spot.spaceType,
      imageURL: spot.photos?.map(p => p.url) || [],
      photos: spot.photos || []
    }));
  } catch (error) {
    console.error('Error fetching initial spots:', error);
    return [];
  }
};

/**
 * Hook to fetch and filter study spots from Supabase
 * @param {Object} filters - Filter options
 * @returns {Object} { spots, loading }
 */
export const useStudySpots = (filters) => {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSpots = async () => {
      setLoading(true);
      try {
        // Base query with relations
        let query = supabase
          .from('StudySpot')
          .select(`
            *,
            building:Building(*),
            photos:Photo(*)
          `);

        // Apply noise level filter at database level
        if (filters.noiseLevel) {
          query = query.eq('noiseLevel', filters.noiseLevel);
        }

        // Apply space type filter at database level
        if (filters.spaceType && filters.spaceType.length > 0) {
          query = query.in('spaceType', filters.spaceType);
        }

        // Apply features filter (PostgreSQL array contains)
        if (filters.features && filters.features.length > 0) {
          // For each required feature, check if it's contained in the features array
          filters.features.forEach(feature => {
            query = query.contains('features', [feature]);
          });
        }

        const { data, error } = await query;

        if (error) throw error;

        // Transform and apply client-side filters that can't be done in SQL
        let filteredData = data.map(spot => ({
          id: spot.id,
          name: spot.name,
          building: spot.building?.name || '',
          location: spot.location,
          description: spot.description,
          features: spot.features || [],
          noiseLevel: spot.noiseLevel,
          spaceType: spot.spaceType,
          imageURL: spot.photos?.map(p => p.url) || [],
          photos: spot.photos || []
        }));

        // Apply location filter (client-side for flexibility)
        if (filters.location && filters.location.length > 0) {
          filteredData = filteredData.filter(spot => {
            return filters.location.some(loc => 
              spot.building === loc || 
              spot.location?.includes(loc) ||
              spot.name?.includes(loc)
            );
          });
        }

        setSpots(filteredData);
      } catch (error) {
        console.error('Error fetching study spots:', error);
        setSpots([]);
      }
      setLoading(false);
    };

    fetchSpots();
  }, [filters]);

  return { spots, loading };
};
